/**
 * ManualCheckpointDialog — place a checkpoint of your own on the route.
 *
 * Two ways to point at a spot, both driving one `distanceM`:
 *
 *   Elevation profile  authoritative. Distance along the route is what the plan
 *                      is built from, and a click on the profile names exactly
 *                      one distance.
 *   Map                confirming view and coarse picker. A map click is
 *                      projected onto the nearest route point — which is
 *                      genuinely ambiguous on a loop or out-and-back, where one
 *                      place sits at two distances, so the projection is scoped
 *                      to a window around the current distance rather than
 *                      searching the whole track and silently guessing.
 *
 * A manual checkpoint is a timing-style split and nothing more: a named place
 * that divides the plan into legs. It carries no stop time and no services —
 * it adds no time to the plan, so a duration field here would imply an effect
 * it cannot have.
 *
 * The name is proposed from the same OpenStreetMap data the plan builder uses to
 * label climbs and descents, so a checkpoint the user places reads like the rest
 * of the plan. The lookup is cache-only server-side and always optional: if
 * nothing is nearby the field simply stays empty and the builder's own
 * "Km 12.4" fallback applies.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Slider, Autocomplete, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip as LeafletTooltip, useMapEvents } from 'react-leaflet';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { getCheckpointNameSuggestion } from '../../api/runningApi';

// A manual checkpoint closer than this to an existing boundary produces a
// segment the plan builder can never merge away (its merge step refuses to
// merge across a checkpoint), so the user is warned before they save one.
const SLIVER_WARN_M = 600;

// Must match _MANUAL_END_MARGIN_M in running_race_plan.py. Closer than this and
// the server drops the point — it would otherwise deduplicate the Start or
// Finish boundary out of the plan entirely. Checked here so the rejection is
// immediate and explains itself, rather than the save appearing to succeed and
// the checkpoint quietly never showing up.
const END_MARGIN_M = 250;

/** Flatten chunk points into an ordered [{lat, lon, distM}] track. */
const buildTrack = (chunks) => {
  const track = [];
  (chunks || []).forEach((chunk) => {
    (chunk.points || []).forEach((pt) => {
      if (pt.lat == null || pt.lon == null) return;
      track.push({ lat: pt.lat, lon: pt.lon, distM: pt.cumulative_distance || 0 });
    });
  });
  return track;
};

/** Elevation profile keyed on cumulative distance, always in metres/km. */
const buildProfile = (chunks) => {
  let cum = 0;
  return (chunks || []).map((chunk) => {
    cum += chunk.distance || 0;
    return { distKm: cum / 1000, altitude: chunk.avg_altitude ?? null };
  });
};

function MapClickHandler({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

export default function ManualCheckpointDialog({
  open,
  onClose,
  onSave,              // async (checkpoint, { addAnother }) => void
  sessionId,           // analysis session, used for name suggestions
  chunks,
  totalDistanceM,
  existingBoundaries = [],   // [{distance_m, label}] — race checkpoints + climb/descent edges
  initial = null,            // editing an existing manual checkpoint
}) {
  const { t } = useTranslation();

  const track = useMemo(() => buildTrack(chunks), [chunks]);
  const profile = useMemo(() => buildProfile(chunks), [chunks]);
  const maxDistM = totalDistanceM || (track.length ? track[track.length - 1].distM : 0);

  const [distanceM, setDistanceM] = useState(initial?.distance_m ?? Math.round(maxDistM / 2));
  const [name, setName] = useState(initial?.name || '');
  // Nothing is drawn until the user picks a spot. Opening with a marker already
  // on the route read as "there is a checkpoint here" rather than "this is where
  // yours will go" — the one thing the dialog must not be ambiguous about.
  // Editing starts placed, because that checkpoint genuinely exists.
  const [placed, setPlaced] = useState(Boolean(initial));
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // True once the user edits the name themselves — after that a moving marker
  // must not overwrite what they typed.
  const nameTouched = useRef(Boolean(initial?.name));

  useEffect(() => {
    if (!open) return;
    setDistanceM(initial?.distance_m ?? Math.round(maxDistM / 2));
    setName(initial?.name || '');
    setError('');
    setPlaced(Boolean(initial));
    nameTouched.current = Boolean(initial?.name);
  }, [open, initial, maxDistM]);

  // Ask for a name whenever the marker settles. Debounced because this fires on
  // every drag tick, and skipped entirely once the user has typed their own.
  useEffect(() => {
    if (!open || !sessionId || !placed) return;
    const handle = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const data = await getCheckpointNameSuggestion(sessionId, Math.round(distanceM));
        setSuggestion(data);
        if (!nameTouched.current) setName(data?.name || '');
      } catch {
        // A missing suggestion is not an error the user needs to see — the name
        // is optional and the builder falls back to a distance label.
        setSuggestion(null);
      } finally {
        setSuggestLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [open, sessionId, distanceM, placed]);

  // Nearest track point to the current distance — drives the map marker.
  const marker = useMemo(() => {
    if (!track.length) return null;
    let best = track[0];
    let bestDiff = Infinity;
    for (const pt of track) {
      const diff = Math.abs(pt.distM - distanceM);
      if (diff < bestDiff) { bestDiff = diff; best = pt; }
    }
    return best;
  }, [track, distanceM]);

  const elevation = useMemo(() => {
    if (!profile.length) return null;
    const km = distanceM / 1000;
    let best = null;
    let bestDiff = Infinity;
    for (const p of profile) {
      const diff = Math.abs(p.distKm - km);
      if (diff < bestDiff) { bestDiff = diff; best = p; }
    }
    return best?.altitude ?? null;
  }, [profile, distanceM]);

  /**
   * Project a map click onto the route.
   *
   * Restricted to a window around the current distance: on a loop course the
   * same coordinates belong to two distances, and picking the globally nearest
   * point would jump the marker across the race with no way for the user to
   * tell it happened. Inside the window the answer is unambiguous, and the
   * profile remains the way to move a long way along the route.
   */
  const handleMapPick = useCallback((lat, lon) => {
    if (!track.length) return;
    // With nothing placed yet there is no current position to disambiguate
    // against, so the whole track is fair game. Once placed, the search is
    // windowed — see the note above about loop courses.
    const WINDOW_M = placed ? 15000 : Infinity;
    let best = null;
    let bestD2 = Infinity;
    for (const pt of track) {
      if (Math.abs(pt.distM - distanceM) > WINDOW_M) continue;
      const dLat = pt.lat - lat;
      const dLon = (pt.lon - lon) * Math.cos((lat * Math.PI) / 180);
      const d2 = dLat * dLat + dLon * dLon;
      if (d2 < bestD2) { bestD2 = d2; best = pt; }
    }
    if (best) {
      nameTouched.current = false;   // a new place deserves a fresh suggestion
      setPlaced(true);
      setDistanceM(best.distM);
    }
  }, [track, distanceM, placed]);

  // Every named boundary in the plan counts here, not just the aid stations.
  // A climb top like "Larga Mică" already divides the plan into legs, prints on
  // the plan sheet and ships to the watch — so when you are deciding where a new
  // checkpoint goes, it is exactly as relevant as a race checkpoint. Treating it
  // as anonymous terrain leaked an internal segment_type distinction into the UI
  // and made a route with eight named boundaries look like it had none.
  const checkpoints = useMemo(
    () => existingBoundaries.filter((b) => (b.label || '').trim()),
    [existingBoundaries],
  );

  // Labels collide once boundaries crowd, so only those far enough apart get
  // one. Spacing scales with the route, and the nearest boundary is always
  // labelled so the marker's context never disappears.
  const labelledDistances = useMemo(() => {
    const minGap = Math.max((maxDistM || 0) / 14, 250);
    const keep = new Set();
    let lastKm = -Infinity;
    for (const b of checkpoints) {
      const d = b.distance_m ?? 0;
      if (d - lastKm >= minGap) { keep.add(d); lastKm = d; }
    }
    return keep;
  }, [checkpoints, maxDistM]);

  // Always say which checkpoint is closest and on which side, so the marker's
  // position is legible even where ticks crowd together.
  const nearestCheckpoint = useMemo(() => {
    let best = null;
    let bestDiff = Infinity;
    for (const cp of checkpoints) {
      const diff = Math.abs((cp.distance_m ?? 0) - distanceM);
      if (diff < bestDiff) { bestDiff = diff; best = cp; }
    }
    if (!best) return null;
    return { ...best, deltaM: distanceM - (best.distance_m ?? 0) };
  }, [checkpoints, distanceM]);

  // Map positions for the existing checkpoints.
  const checkpointMarkers = useMemo(() => {
    if (!track.length) return [];
    return checkpoints.map((cp) => {
      let best = track[0];
      let bestDiff = Infinity;
      for (const pt of track) {
        const diff = Math.abs(pt.distM - (cp.distance_m ?? 0));
        if (diff < bestDiff) { bestDiff = diff; best = pt; }
      }
      return { ...cp, lat: best.lat, lon: best.lon };
    });
  }, [checkpoints, track]);

  // Drives the warning styling on the readout above. Deliberately NOT an alert
  // that mounts and unmounts: this toggles constantly while the marker moves,
  // and a block appearing mid-dialog reflowed everything under it on every drag.
  const tooClose = useMemo(
    () => existingBoundaries.some(
      (b) => Math.abs((b.distance_m ?? 0) - distanceM) < SLIVER_WARN_M),
    [existingBoundaries, distanceM],
  );

  const handleSave = async (addAnother) => {
    setError('');
    if (distanceM < END_MARGIN_M || distanceM > maxDistM - END_MARGIN_M) {
      setError(t('checkpointTooCloseToEnds',
        'Keep it at least {{m}} m from the start and finish — any closer and it merges into those boundaries instead of becoming its own.',
        { m: END_MARGIN_M }));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        id: initial?.id,
        name: name.trim(),
        distance_m: Math.round(distanceM),
      }, { addAnother });
      if (addAnother) {
        nameTouched.current = false;
        setName('');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : t('checkpointSaveFailed', 'Could not save the checkpoint. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const candidateOptions = (suggestion?.candidates || []).map((c) => ({
    label: c.name,
    detail: `${c.type?.replace(/_/g, ' ') || c.class} · ${c.distance_m} m`,
  }));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlaceIcon fontSize="small" />
        {initial
          ? t('editCheckpoint', 'Edit checkpoint')
          : t('addCheckpoint', 'Add checkpoint')}
      </DialogTitle>

      <DialogContent>
        {/* Readout first: the two numbers that say where you are. */}
        <Box display="flex" alignItems="baseline" gap={2} mb={1}>
          {!placed && (
            <Typography variant="h6" color="text.secondary">
              {t('clickToPlace', 'Click the profile or the map to place it')}
            </Typography>
          )}
          {placed && (
            <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('km', 'km')} {(distanceM / 1000).toFixed(1)}
            </Typography>
          )}
          {placed && elevation != null && (
            <Typography variant="body2" color="text.secondary"
              sx={{ fontVariantNumeric: 'tabular-nums' }}>
              ↑ {Math.round(elevation)} m
            </Typography>
          )}
          {/* Named distance to the closest existing checkpoint. The ticks alone
              cannot answer "how far am I from that aid station?" once they
              crowd, and that is the question being asked here. */}
          {placed && !nearestCheckpoint && (
            <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
              {t('noCheckpointsOnRoute',
                 'Nothing named on this route yet — this would be the first.')}
            </Typography>
          )}
          {placed && nearestCheckpoint && (
            <Tooltip
              title={tooClose
                ? t('checkpointNearBoundaryWhy',
                    'Two checkpoints this close leave a segment too short for the plan to merge away. Move it further along, or use the existing one.')
                : ''}
            >
              <Typography
                variant="body2"
                sx={{ ml: 'auto', color: tooClose ? 'warning.main' : 'text.secondary',
                      fontWeight: tooClose ? 600 : 400 }}
              >
                {tooClose ? '⚠ ' : ''}
                {nearestCheckpoint.label}
                {' · '}
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Math.abs(nearestCheckpoint.deltaM) < 1000
                    ? `${Math.round(Math.abs(nearestCheckpoint.deltaM))} m`
                    : `${(Math.abs(nearestCheckpoint.deltaM) / 1000).toFixed(1)} km`}
                </Box>
                {' '}
                {nearestCheckpoint.deltaM >= 0
                  ? t('afterIt', 'after it')
                  : t('beforeIt', 'before it')}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Elevation profile — the authoritative way to choose a distance. */}
        <Box sx={{ height: 172, mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={profile}
              margin={{ top: 26, right: 12, bottom: 4, left: 0 }}
              onClick={(e) => {
                const km = e?.activeLabel;
                if (km == null) return;
                nameTouched.current = false;
                setPlaced(true);
                setDistanceM(Number(km) * 1000);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="distKm" type="number" domain={['dataMin', 'dataMax']}
                tickFormatter={(v) => v.toFixed(0)} fontSize={11} />
              <YAxis fontSize={11} width={44}
                tickFormatter={(v) => (v == null ? '' : Math.round(v))} />
              <Line type="monotone" dataKey="altitude" dot={false}
                stroke="#42a5f5" strokeWidth={1.5} isAnimationActive={false} />
              {/* Every named boundary already in the plan. Colour says where it
                  came from; the dotted line and name say "something is here". */}
              {checkpoints.map((b, i) => {
                const colour = b.kind === 'manual' ? '#ab47bc'
                  : b.kind === 'checkpoint' ? '#26a69a'
                  : '#90a4ae';
                const isNearest = nearestCheckpoint
                  && nearestCheckpoint.distance_m === b.distance_m;
                const showLabel = labelledDistances.has(b.distance_m ?? 0) || isNearest;
                return (
                  <ReferenceLine
                    key={`b${i}`}
                    x={(b.distance_m ?? 0) / 1000}
                    stroke={colour}
                    strokeWidth={b.kind === 'terrain' ? 1 : 1.5}
                    strokeDasharray="4 3"
                    opacity={b.kind === 'terrain' ? 0.75 : 1}
                    label={showLabel ? {
                      value: b.label,
                      position: 'top',
                      fontSize: 9,
                      fill: colour,
                    } : undefined}
                  />
                );
              })}
              {placed && (
                <ReferenceLine x={distanceM / 1000} stroke="#ff9800" strokeWidth={2} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box display="flex" gap={2} alignItems="center" mb={0.5} flexWrap="wrap">
            {checkpoints.some((c) => c.kind === 'checkpoint') && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 12, height: 2, bgcolor: '#26a69a' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('raceCheckpoints', 'Race checkpoints')}
                </Typography>
              </Box>
            )}
            {checkpoints.some((c) => c.kind === 'terrain') && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 12, height: 2, bgcolor: '#90a4ae' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('planBoundaries', 'Plan boundaries')}
                </Typography>
              </Box>
            )}
            {checkpoints.some((c) => c.kind === 'manual') && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 12, height: 2, bgcolor: '#ab47bc' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('yourCheckpoints', 'Yours')}
                </Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 12, height: 2, bgcolor: '#ff9800' }} />
              <Typography variant="caption" color="text.secondary">
                {t('thisCheckpoint', 'This one')}
              </Typography>
            </Box>
        </Box>

        <Slider
          size="small"
          value={distanceM}
          min={0}
          max={maxDistM || 1}
          step={10}
          onChange={(_, v) => { nameTouched.current = false; setPlaced(true); setDistanceM(v); }}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${(v / 1000).toFixed(1)} km`}
          sx={{ mb: 1 }}
        />

        {/* Map — confirms the spot, and can nudge it locally. */}
        {track.length > 0 && marker && (
          <Box sx={{ height: 220, mb: 2, borderRadius: 1, overflow: 'hidden' }}>
            <MapContainer center={[marker.lat, marker.lon]} zoom={13}
              style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Polyline positions={track.map((p) => [p.lat, p.lon])}
                pathOptions={{ color: '#42a5f5', weight: 3, opacity: 0.8 }} />
              {checkpointMarkers.map((cp, i) => (
                <CircleMarker key={i} center={[cp.lat, cp.lon]} radius={5}
                  pathOptions={{
                    color: cp.kind === 'manual' ? '#ab47bc'
                      : cp.kind === 'checkpoint' ? '#26a69a' : '#90a4ae',
                    fillColor: cp.kind === 'manual' ? '#ab47bc'
                      : cp.kind === 'checkpoint' ? '#26a69a' : '#90a4ae',
                    fillOpacity: 0.9,
                  }}>
                  <LeafletTooltip direction="top">
                    {cp.label} · {t('km', 'km')} {((cp.distance_m ?? 0) / 1000).toFixed(1)}
                  </LeafletTooltip>
                </CircleMarker>
              ))}
              {/* Drawn last so it stays on top of the existing ones. */}
              {placed && (
                <CircleMarker center={[marker.lat, marker.lon]} radius={8}
                  pathOptions={{ color: '#ff9800', fillColor: '#ff9800', fillOpacity: 1 }} />
              )}
              <MapClickHandler onPick={handleMapPick} />
            </MapContainer>
          </Box>
        )}

        <Autocomplete
          freeSolo
          options={candidateOptions}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
          value={name}
          onInputChange={(_, v) => { nameTouched.current = true; setName(v); }}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{option.label}</span>
              <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('checkpointName', 'Name')}
              placeholder={t('checkpointNamePlaceholder', 'e.g. Crew — shoes + poles')}
              helperText={
                suggestLoading
                  ? t('lookingUpName', 'Looking up nearby places…')
                  : suggestion?.candidates?.length
                  ? t('nameSuggestedFromMap', 'Suggested from the map — pick another or type your own.')
                  : t('noNearbyPlaces', 'No mapped places near here — give it your own name.')
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {suggestLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('cancel', 'Cancel')}</Button>
        {!initial && (
          <Button onClick={() => handleSave(true)} disabled={saving || !placed}>
            {t('saveAndAddAnother', 'Save & add another')}
          </Button>
        )}
        <Button variant="contained" onClick={() => handleSave(false)} disabled={saving || !placed}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {t('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
